import React from 'react';
import { Button, Col, FormGroup, Input, Label, Row } from 'reactstrap';
import currencyFormatter from '../../../utils/currencyFormatter';
import {
  enrichCadastroSectionsWithLookups,
  getCadastroSections,
  getPropostaDocuments,
  getPropostaValorFromInfo,
} from './contrFornCompraCotacaoInfo';

const EMPTY_SECTIONS = getCadastroSections(null);

const fieldLabelStyle = {
  fontSize: '12px',
  marginBottom: '2px',
};

const fieldValueStyle = {
  fontWeight: 'bold',
  marginBottom: '8px',
};

const sectionDividerStyle = {
  borderTop: '1px solid #e6e6e6',
  margin: '0 0rem 1.25rem',
};

function SectionBlock({ title, children, showTopDivider = false }) {
  return (
    <div className="mb-4">
      {showTopDivider ? <div style={sectionDividerStyle} aria-hidden="true" /> : null}
      {title ? (
        <h5 className="mb-3" style={{ fontWeight: 600 }}>
          {title}
        </h5>
      ) : null}
      {children}
    </div>
  );
}

function FieldItem({ label, value, md = 3 }) {
  return (
    <Col md={md}>
      <Label className="text-muted" style={fieldLabelStyle}>{label}</Label>
      <p style={fieldValueStyle}>{value == null || value === '' ? '-' : value}</p>
    </Col>
  );
}

function InfoSection({ title, entries = [], showTopDivider = false }) {
  if (!entries.length) return null;

  return (
    <SectionBlock title={title} showTopDivider={showTopDivider}>
      <Row>
        {entries.map((entry) => (
          <FieldItem
            key={`${title}-${entry.key}`}
            label={entry.label}
            value={entry.value}
          />
        ))}
      </Row>
    </SectionBlock>
  );
}

function DocumentosSection({ documentos = [], showTopDivider = false }) {
  if (!documentos.length) return null;

  return (
    <SectionBlock title="Documentos obrigatórios" showTopDivider={showTopDivider}>
      <Row>
        {documentos.map((doc) => (
          <Col key={doc.key} md={3} className="mb-2">
            <Label className="text-muted" style={fieldLabelStyle}>{doc.label}</Label>
            <div style={{ marginBottom: '8px' }}>
              {doc.link ? (
                <Button color="primary" size="sm" tag="a" href={doc.link} target="_blank" rel="noopener noreferrer">
                  Visualizar
                </Button>
              ) : (
                <p className="text-muted mb-0" style={{ fontSize: '13px' }}>Sem link</p>
              )}
            </div>
          </Col>
        ))}
      </Row>
    </SectionBlock>
  );
}

function PropostaSection({ parsedInfo, sourceDocuments = [], showTopDivider = false }) {
  const propostaDocuments = React.useMemo(
    () => getPropostaDocuments(sourceDocuments, parsedInfo),
    [sourceDocuments, parsedInfo],
  );
  const propostaValor = React.useMemo(
    () => getPropostaValorFromInfo(parsedInfo),
    [parsedInfo],
  );
  const objetoResumido = React.useMemo(() => {
    const value = parsedInfo?.informacoes_contrato?.objeto_resumido;
    return value != null && value !== '' ? String(value).trim() : '-';
  }, [parsedInfo]);

  return (
    <SectionBlock title="Proposta" showTopDivider={showTopDivider}>
      <Row>
        <Col md={12} className="mb-2">
          <Label className="text-muted" style={fieldLabelStyle}>Documentos da proposta</Label>
          {propostaDocuments.length > 0 ? (
            <div className="mb-2">
              {propostaDocuments.map((doc) => (
                <div
                  key={`${doc.documento_id || doc.label}-${doc.source}`}
                  className="d-flex justify-content-between align-items-center mb-2"
                >
                  <span style={{ fontWeight: 'bold' }}>{doc.label}</span>
                  {doc.link ? (
                    <Button color="primary" size="sm" tag="a" href={doc.link} target="_blank" rel="noopener noreferrer">
                      Visualizar
                    </Button>
                  ) : (
                    <span className="text-muted" style={{ fontSize: '13px' }}>Sem link</span>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <p style={fieldValueStyle}>Nenhum documento de proposta encontrado.</p>
          )}
        </Col>

        <FieldItem
          label="Valor"
          value={currencyFormatter.format(Number(propostaValor) || 0)}
          md={3}
        />

        <Col md={12}>
          <Label className="text-muted" style={fieldLabelStyle}>Objeto resumido</Label>
          <p style={{ ...fieldValueStyle, whiteSpace: 'pre-wrap' }}>{objetoResumido}</p>
        </Col>
      </Row>
    </SectionBlock>
  );
}

function ContrFornValidationStepCadastro({
  parsedInfo,
  sourceDocuments = [],
  validationAnswer = null,
  onValidationAnswerChange = () => {},
  rejectionJustification = '',
  onRejectionJustificationChange = () => {},
}) {
  const [sections, setSections] = React.useState(() => getCadastroSections(parsedInfo));

  React.useEffect(() => {
    let cancelled = false;

    const loadSections = async () => {
      if (!parsedInfo) {
        setSections(EMPTY_SECTIONS);
        return;
      }

      // Mostra valores brutos imediatamente; depois troca códigos por nomes.
      setSections(getCadastroSections(parsedInfo));
      try {
        const enriched = await enrichCadastroSectionsWithLookups(parsedInfo);
        if (!cancelled) setSections(enriched);
      } catch (error) {
        console.error('Erro ao resolver nomes de banco/pixTp:', error);
      }
    };

    loadSections();
    return () => { cancelled = true; };
  }, [parsedInfo]);

  const isPjCadastro = sections.cadastroTipo === 'pj';
  const primaryCadastroSection = isPjCadastro ? sections.informacoesEmpresa : sections.dadosPessoais;
  const primaryCadastroTitle = isPjCadastro ? 'Informações da empresa' : 'Dados pessoais';

  const hasAnySection = primaryCadastroSection.length > 0
    || sections.representanteLegal.length > 0
    || sections.gestorContrato.length > 0
    || sections.endereco.length > 0
    || sections.dadosBancarios.length > 0
    || sections.documentosObrigatorios.length > 0;

  const showPjContactsRow = isPjCadastro && (
    sections.representanteLegal.length > 0 || sections.gestorContrato.length > 0
  );
  const showSecondRow = sections.endereco.length > 0 || sections.dadosBancarios.length > 0;

  const sectionNodes = [
    primaryCadastroSection.length > 0 ? (
      <InfoSection
        key="primary-cadastro"
        title={primaryCadastroTitle}
        entries={primaryCadastroSection}
      />
    ) : null,
    showPjContactsRow && sections.representanteLegal.length > 0 ? (
      <InfoSection
        key="representante-legal"
        title="Representante legal"
        entries={sections.representanteLegal}
      />
    ) : null,
    showPjContactsRow && sections.gestorContrato.length > 0 ? (
      <InfoSection
        key="gestor-contrato"
        title="Gestor do contrato"
        entries={sections.gestorContrato}
      />
    ) : null,
    showSecondRow && sections.endereco.length > 0 ? (
      <InfoSection
        key="endereco"
        title="Endereço"
        entries={sections.endereco}
      />
    ) : null,
    showSecondRow && sections.dadosBancarios.length > 0 ? (
      <InfoSection
        key="dados-bancarios"
        title="Dados bancários"
        entries={sections.dadosBancarios}
      />
    ) : null,
    sections.documentosObrigatorios.length > 0 ? (
      <DocumentosSection
        key="documentos"
        documentos={sections.documentosObrigatorios}
      />
    ) : null,
    <PropostaSection
      key="proposta"
      parsedInfo={parsedInfo}
      sourceDocuments={sourceDocuments}
    />,
    <SectionBlock key="confirmacao" title="Confirmar validação dos dados cadastrais e a proposta?">
      <div className="d-flex flex-wrap align-items-center">
        <FormGroup check inline className="mb-0 mr-4">
          <Label check>
            <Input
              type="radio"
              name="step1ValidationAnswer"
              checked={validationAnswer === 'sim'}
              onChange={() => onValidationAnswerChange('sim')}
            />
            {' '}
            Sim
          </Label>
        </FormGroup>
        <FormGroup check inline className="mb-0">
          <Label check>
            <Input
              type="radio"
              name="step1ValidationAnswer"
              checked={validationAnswer === 'nao'}
              onChange={() => onValidationAnswerChange('nao')}
            />
            {' '}
            Não
          </Label>
        </FormGroup>
      </div>

      {validationAnswer === 'nao' && (
        <FormGroup className="mt-3 mb-0">
          <Label
            for="step1RejectionJustification"
            className="text-muted"
            style={fieldLabelStyle}
          >
            Justificativa da rejeição
          </Label>
          <Input
            id="step1RejectionJustification"
            type="textarea"
            rows={4}
            value={rejectionJustification}
            onChange={(e) => onRejectionJustificationChange(e.target.value)}
          />
        </FormGroup>
      )}
    </SectionBlock>,
  ].filter(Boolean);

  return (
    <>
      {!parsedInfo && (
        <div className="alert alert-warning mb-3" role="alert">
          Nenhuma informação cadastral foi encontrada em <code>compraCotacao_info</code>.
          Verifique se o fornecedor concluiu o envio e se o registro possui JSON válido no banco.
        </div>
      )}

      {parsedInfo && !hasAnySection && (
        <div className="alert alert-warning mb-3" role="alert">
          O JSON de <code>compraCotacao_info</code> foi carregado, mas não contém blocos cadastrais reconhecidos.
        </div>
      )}

      {sectionNodes.map((node, index) => (
        React.cloneElement(node, { showTopDivider: index > 0 })
      ))}
    </>
  );
}

export default ContrFornValidationStepCadastro;
